import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { CheckCircle2, X } from 'lucide-react';
import { CRUDPage } from '../../components/crudPage.tsx';
import { RecoveryTaskForm } from '../../components/forms/recoveryTaskForm.tsx';
import { useCompany } from "../../context/CompanyContext.tsx";
import { getToken } from '../../utils/auth.ts';
import axiosInstance from '../../utils/axiosConfig.ts';
import { toast } from "../../utils/notify.ts";
import {
  canOwnerSettleRecovery,
  recoveryStatusLabel,
} from '../../utils/recoveryStatus.ts';

interface RecoveryTask {
  id: string;
  invoice_id: string;
  invoice_number: string;
  customer_name: string;
  customer_internet_id: string;
  total_amount: number;
  collected_amount?: number | null;
  payment_id?: string | null;
  payment_status?: string | null;
  payment_method?: string | null;
  payment_proof?: string | null;
  has_payment_proof?: boolean;
  assigned_to: string;
  assigned_to_name: string;
  status: string;
  notes: string;
  created_at: string;
  updated_at: string;
  is_assigned?: boolean;
  settled_at?: string | null;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200';
    case 'in_progress': return 'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-200';
    case 'collected': return 'bg-orange-50 text-orange-800 ring-1 ring-inset ring-orange-200';
    case 'completed': return 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200';
    case 'cancelled': return 'bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200';
    default: return 'bg-slate-50 text-slate-600 ring-1 ring-inset ring-slate-200';
  }
};

const RecoveryTaskManagement: React.FC = () => {
  const { setPageTitle } = useCompany();
  const [settleTarget, setSettleTarget] = useState<RecoveryTask | null>(null);
  const [bulkSettle, setBulkSettle] = useState<{
    items: RecoveryTask[];
    clearSelection: () => void;
    refresh: () => Promise<void>;
  } | null>(null);
  const [settling, setSettling] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [proofUrl, setProofUrl] = useState<string | null>(null);
  const [proofMime, setProofMime] = useState<string | null>(null);
  const [proofLoading, setProofLoading] = useState(false);

  useEffect(() => {
    setPageTitle("Recovery Tasks");
  }, [setPageTitle]);

  useEffect(() => {
    let objectUrl: string | null = null;
    const loadProof = async () => {
      setProofUrl(null);
      setProofMime(null);
      if (!settleTarget?.payment_id || !settleTarget.has_payment_proof) {
        setProofLoading(false);
        return;
      }
      setProofLoading(true);
      try {
        const token = getToken();
        const res = await axiosInstance.get(`/payments/proof-image/${settleTarget.payment_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        objectUrl = URL.createObjectURL(res.data);
        setProofUrl(objectUrl);
        setProofMime(res.data.type || null);
      } catch {
        setProofUrl(null);
        setProofMime(null);
      } finally {
        setProofLoading(false);
      }
    };
    loadProof();
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [settleTarget]);

  const settleOne = async (id: string) => {
    const token = getToken();
    await axiosInstance.post(
      `/recovery-tasks/${id}/settle`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const handleSettle = async () => {
    if (!settleTarget) return;
    setSettling(true);
    try {
      await settleOne(settleTarget.id);
      toast.success("Recovery settled successfully");
      setSettleTarget(null);
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.response?.data?.error || "Failed to settle recovery");
    } finally {
      setSettling(false);
    }
  };

  const handleBulkSettle = async () => {
    if (!bulkSettle?.items.length) return;
    setSettling(true);
    try {
      const results = await Promise.allSettled(bulkSettle.items.map((item) => settleOne(item.id)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok > 0) {
        toast.success(ok === 1 ? "1 recovery settled" : `${ok} recoveries settled`);
      }
      if (failed > 0) {
        toast.error(`${failed} could not be settled`);
      }
      await bulkSettle.refresh();
      bulkSettle.clearSelection();
      setBulkSettle(null);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.response?.data?.error || "Failed to settle recoveries");
    } finally {
      setSettling(false);
    }
  };

  const bulkTotal = useMemo(() => {
    if (!bulkSettle) return 0;
    return bulkSettle.items.reduce((sum, item) => sum + Number(item.collected_amount ?? 0), 0);
  }, [bulkSettle]);

  const columns = useMemo<ColumnDef<RecoveryTask>[]>(
    () => [
      {
        header: 'Invoice',
        accessorKey: 'invoice_number',
        cell: info => {
          const row = info.row.original;
          return (
            <div className="flex flex-col gap-0.5 min-w-[140px]">
              {row.invoice_id ? (
                <Link
                  to={`/invoices/${row.invoice_id}`}
                  className="text-electric-blue font-medium hover:underline text-sm whitespace-nowrap"
                  title={`View invoice ${row.invoice_number}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.invoice_number || '—'}
                </Link>
              ) : (
                <span className="font-medium text-deep-ocean text-sm">{row.invoice_number || '—'}</span>
              )}
              <span className="text-xs text-slate-600 truncate max-w-[200px]" title={row.customer_name}>
                {row.customer_name || '—'}
              </span>
              {row.customer_internet_id && (
                <span className="text-[11px] text-slate-400 font-mono">{row.customer_internet_id}</span>
              )}
            </div>
          );
        },
      },
      {
        header: 'Amount',
        accessorKey: 'total_amount',
        cell: info => {
          const row = info.row.original;
          const value = info.getValue() as number | null;
          if (value == null) return <span className="text-gray-400">—</span>;
          return (
            <div className="flex flex-col text-sm tabular-nums">
              <span className="font-semibold text-deep-ocean">
                PKR {Number(value).toLocaleString()}
              </span>
              {row.collected_amount != null && canOwnerSettleRecovery(row.status) && (
                <span className="text-[11px] text-orange-700">
                  Collected {Number(row.collected_amount).toLocaleString()}
                </span>
              )}
            </div>
          );
        },
      },
      {
        header: 'Assigned To',
        accessorKey: 'assigned_to_name',
        cell: info => <span className="text-sm text-slate-700">{(info.getValue() as string) || '—'}</span>,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: info => {
          const value = info.getValue() as string | null;
          if (!value) return <span className="text-gray-400">—</span>;
          return (
            <span className={`inline-flex px-2 py-0.5 text-[11px] leading-4 font-medium rounded-md ${getStatusColor(value)}`}>
              {recoveryStatusLabel(value)}
            </span>
          );
        },
      },
      {
        header: 'Notes',
        accessorKey: 'notes',
        cell: info => {
          const value = info.getValue() as string | null;
          if (!value) return <span className="text-slate-300">—</span>;
          return (
            <div className="max-w-[160px] truncate text-sm text-slate-600" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        header: 'Created',
        accessorKey: 'created_at',
        cell: info => {
          const value = info.getValue() as string | null;
          if (!value) return <span className="text-gray-400">—</span>;
          return <span className="text-sm text-slate-600 whitespace-nowrap">{new Date(value).toLocaleDateString()}</span>;
        },
      },
    ],
    []
  );

  return (
    <div className="relative">
      <CRUDPage<RecoveryTask>
        key={refreshKey}
        title="Recovery Task"
        endpoint="recovery-tasks"
        filterModuleKey="recovery-task"
        columns={columns}
        FormComponent={RecoveryTaskForm}
        renderRowActions={(item) =>
          canOwnerSettleRecovery(item.status) ? (
            <button
              type="button"
              onClick={() => setSettleTarget(item)}
              className="h-8 px-2.5 text-xs font-medium bg-amber-600 text-white rounded-md hover:bg-amber-700 whitespace-nowrap"
              title="Settle collection"
            >
              Settle
            </button>
          ) : null
        }
        renderBulkActions={({ selectedItems, clearSelection, refresh, isLoading }) => {
          const allCollected =
            selectedItems.length > 0 &&
            selectedItems.every((item) => canOwnerSettleRecovery(item.status));
          if (!allCollected) return null;
          return (
            <button
              type="button"
              disabled={isLoading || settling}
              onClick={() => setBulkSettle({ items: selectedItems, clearSelection, refresh })}
              className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-600 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              <CheckCircle2 className="h-4 w-4" /> Settle selected
            </button>
          );
        }}
        validateBeforeSubmit={(fd) => {
          const data = fd as RecoveryTask & { invoice_ids?: string[] }
          if (!data.assigned_to) return "Select an employee"
          // Edit keeps single invoice_id; create uses invoice_ids.
          if (Array.isArray(data.invoice_ids)) {
            if (data.invoice_ids.length === 0) return "Select at least one invoice"
          } else if (!data.invoice_id) {
            return "Select an invoice"
          }
          return null
        }}
        onCreateSubmit={async (fd) => {
          const data = fd as RecoveryTask & { invoice_ids?: string[] }
          const ids =
            Array.isArray(data.invoice_ids) && data.invoice_ids.length > 0
              ? data.invoice_ids
              : data.invoice_id
                ? [data.invoice_id]
                : []
          if (ids.length === 0) return false

          const token = getToken()
          const res = await axiosInstance.post(
            "/recovery-tasks/bulk-add",
            {
              invoice_ids: ids,
              assigned_to: data.assigned_to,
              notes: data.notes,
              status: data.status || "pending",
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          const body = res.data || {}
          const created = Number(body.total_created || 0)
          const skipped = Number(body.total_skipped || 0)
          if (created === 0) {
            const reason = body.skipped?.[0]?.reason || "No recovery tasks created"
            throw new Error(reason)
          }
          if (skipped > 0) {
            toast.success(`Created ${created}; skipped ${skipped}`)
          } else {
            toast.success(
              created === 1
                ? "Recovery task added successfully"
                : `Created ${created} recovery tasks`
            )
          }
          return true
        }}
      />

      {settleTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-[2px] p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-base font-semibold text-deep-ocean">Settle collection</h3>
              <button type="button" onClick={() => setSettleTarget(null)} className="p-1 rounded hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-2 text-sm max-h-[70vh] overflow-y-auto">
              <p><span className="text-slate-500">Invoice:</span> {settleTarget.invoice_number}</p>
              <p><span className="text-slate-500">Collector:</span> {settleTarget.assigned_to_name}</p>
              <p>
                <span className="text-slate-500">Amount:</span>{' '}
                <span className="font-semibold">
                  PKR {Number(settleTarget.collected_amount ?? 0).toLocaleString()}
                </span>
              </p>
              <p>
                <span className="text-slate-500">Method:</span>{' '}
                {(settleTarget.payment_method || '—').replace(/_/g, ' ')}
              </p>

              {settleTarget.has_payment_proof && (
                <div className="pt-2">
                  <p className="text-xs font-medium text-slate-600 mb-1.5">Payment proof</p>
                  {proofLoading ? (
                    <p className="text-xs text-slate-500">Loading proof…</p>
                  ) : proofUrl ? (
                    proofMime === 'application/pdf' ? (
                      <a
                        href={proofUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-9 items-center px-3 text-xs font-medium bg-electric-blue text-white rounded-md"
                      >
                        Open PDF proof
                      </a>
                    ) : (
                      <a href={proofUrl} target="_blank" rel="noopener noreferrer" className="block">
                        <img
                          src={proofUrl}
                          alt="Payment proof"
                          className="max-h-56 w-full object-contain rounded-lg border border-slate-200 bg-slate-50"
                        />
                      </a>
                    )
                  ) : (
                    <p className="text-xs text-slate-500">Proof attached but could not be previewed.</p>
                  )}
                </div>
              )}

              <p className="text-xs text-slate-500 pt-1">
                Marks payment paid, clears employee cash hold, and credits bank if transfer.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
              <button
                type="button"
                onClick={() => setSettleTarget(null)}
                className="h-9 px-3 text-sm border border-slate-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={settling}
                onClick={handleSettle}
                className="h-9 px-3 text-sm bg-amber-600 text-white rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <CheckCircle2 className="h-4 w-4" />
                {settling ? 'Settling…' : 'Confirm settle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkSettle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-[2px] p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-base font-semibold text-deep-ocean">Settle selected</h3>
              <button type="button" onClick={() => setBulkSettle(null)} className="p-1 rounded hover:bg-slate-100">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="p-4 space-y-2 text-sm">
              <p>
                Settle <span className="font-semibold">{bulkSettle.items.length}</span> collected
                {bulkSettle.items.length === 1 ? " recovery" : " recoveries"}?
              </p>
              <p>
                <span className="text-slate-500">Total collected:</span>{' '}
                <span className="font-semibold">PKR {bulkTotal.toLocaleString()}</span>
              </p>
              <p className="text-xs text-slate-500 pt-1">
                Marks each payment paid, clears employee cash holds, and credits bank if transfer.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50">
              <button
                type="button"
                onClick={() => setBulkSettle(null)}
                className="h-9 px-3 text-sm border border-slate-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={settling}
                onClick={handleBulkSettle}
                className="h-9 px-3 text-sm bg-amber-600 text-white rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <CheckCircle2 className="h-4 w-4" />
                {settling ? 'Settling…' : 'Confirm settle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecoveryTaskManagement;
